import { useRef, useEffect } from 'react';
import { useTheme } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';

interface IProps {
  height: number;
  series?: Highcharts.SeriesOptionsType[];
  categories?: Array<string>;
  yAxis?: Highcharts.YAxisOptions;
  xAxis?: Highcharts.XAxisOptions;
  legend?: Highcharts.LegendOptions;
  plotOptions?: Highcharts.PlotOptions;
  loading?: boolean;
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
}

const BarChart = ({
  height,
  series,
  categories,
  yAxis = {},
  xAxis = {},
  legend = {},
  plotOptions = {},
  loading,
  tooltipFormatter,
}: IProps) => {
  const {
    palette: { text, divider },
  } = useTheme();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    if (!chartComponentRef.current?.chart) return;
    if (loading) {
      chartComponentRef.current?.chart.showLoading('Loading data...');
      return;
    }

    chartComponentRef.current?.chart.hideLoading();
  }, [chartComponentRef.current?.chart, loading]);

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      height: height,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: categories,
      labels: {
        style: {
          color: text.secondary,
          fontSize: '14px',
          fontWeight: '400',
        },
      },
      lineColor: divider,
      ...xAxis,
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        style: {
          color: text.secondary,
          fontSize: '14px',
          fontWeight: '400',
        },
      },
      ...yAxis,
    },
    series: series,
    legend: legend,
    plotOptions: {
      column: {
        dataLabels: {
          borderRadius: 10,
          enabled: false, // Hide the value labels on the columns
        },
      },
      ...plotOptions,
    },
    tooltip: {
      formatter: tooltipFormatter,
      backgroundColor: '#000', // Background color of the tooltip
      borderColor: '#000000', // Border color of the tooltip
      borderRadius: 8, // Border radius of the tooltip
      padding: 14,
      style: {
        color: '#fff', // Text color inside the tooltip
        fontSize: '12px',
        lineHeight: '20px',
      },
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />;
};

export default BarChart;
